import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

type Section = 'basic' | 'sales' | 'identity' | 'roles';

@Injectable()
export class UserRegistrationService {
    constructor(private http: HttpClient) {}

    getUser(userId: string, sections: Section[]): Observable<User> {
        let params = new HttpParams();
        sections.forEach((section: string) => {
            params = params.append('sections', section);
        });

        return this.http.get<User>(`${environment.http.usersBaseUrl}users/${userId}`, { params });
    }

    updateUser(userId: string, user: UpdatableUser): Observable<UpdatableUser> {
        return this.http.patch(`${environment.http.usersBaseUrl}users/${userId}`, user);
    }

    changePassword(userId: string, password: { password: string; newPassword: string }) {
        return this.http.patch(`${environment.http.usersBaseUrl}users/${userId}/password`, password);
    }

    isLoginIdTaken(loginId: string): Observable<{ isTaken: boolean; byEndCustomerAccount: boolean }> {
        return this.http
            .get(`${environment.http.usersBaseUrl}users/check-user-login?loginId=${encodeURIComponent(loginId)}`)
            .pipe(
                map((response) => {
                    return {
                        isTaken: response['userAlreadyExists'],
                        byEndCustomerAccount: response['identityType'] === 'EK',
                    };
                })
            );
    }

    sendEmailConfirmation(loginId: string) {
        return this.http.post(`${environment.http.usersBaseUrl}users/send-email-confirmation`, { loginId });
    }

    requestPasswordReset(loginId) {
        return this.http.post(`${environment.http.usersBaseUrl}users/request-password-reset`, { loginId });
    }

    updateContact(contactId: string, contact: UpdatableContact): Observable<void> {
        return this.http.patch<void>(`${environment.http.usersBaseUrl}contacts/${contactId}`, contact);
    }

    deleteMe() {
        return this.http.delete(`${environment.http.usersBaseUrl}users/me`);
    }

    createCustomer(companyId: string, contact: CreatableContact): Observable<{ id: string }> {
        return this.http.post<{ id: string }>(
            `${environment.http.registerBaseUrl}companies/${companyId}/users`,
            contact
        );
    }
}

export interface User {
    id: string;
    loginId: string;
    gender: 'MALE' | 'FEMALE' | 'OTHER';
    name: {
        title: string;
        firstName: string;
        familyName: string;
    };
    address: {
        street: string;
        houseNumber: string;
        postalCode: string;
        city: string;
        countryCode: string;
    };
    company: {
        name: string;
        countryCode: string;
        id: string;
        vkorg: string;
    };
    contacts: {
        email: string;
        telephone?: string;
        mobile?: string;
    };
    userState: string;
    languageCode: string;
    properties: { name: string; value: string }[];
    applicationRoles?: string[];
    businessRoes?: string[];
    contactId?: string;
}

export interface UpdatableUser {
    loginId?: string;
    userState?: 'DISABLED' | 'ACTIVE';
    name?: {
        firstName?: string;
        familyName?: string;
    };
    address?: {
        street?: string;
        houseNumber?: string;
        postalCode?: string;
        city?: string;
        countryCode?: string;
        region?: string;
    };
    contacts?: {
        email?: string;
        telephone?: string;
        mobile?: string;
    };
}

export interface UpdatableContact {
    firstName?: string;
    email?: string;
    mobilePhone?: {
        countryCode: string;
        number: string;
    };
    phone?: {
        countryCode: string;
        number: string;
        extension?: string;
    };
    prioRouting?: boolean;
}

export interface CreatableContact {
    firstName: string;
    lastName: string;
    email: string;
    salutation: 'MR' | 'MRS';
    acceptedDocuments: string[];
    id?: string;
}
